// angular imports
import { Component, OnInit, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

// devextreme imports
import { DxFileUploaderComponent } from 'devextreme-angular';

// cc-common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// component imports
import { PropertyCommunityDocumentsComponent } from '../../property-community-documents/property-community-documents.component';

// service imports
import { FileReaderService } from '../../../../../_services/shared-services/file-reader.service';

// model imports
import { AttachmentRawDto } from '../../../../_models/attachment-raw-dto';

// constant imports
import { ActionConstants } from '../../../../../_constants/action-constants';
import { EmailConstants } from '../../../../../_constants/email-constants';

@Component({
    selector: 'app-attachment-editor',
    templateUrl: './attachment-editor.component.html',
    styleUrls: ['./attachment-editor.component.scss']
})
export class AttachmentEditorComponent implements OnInit, OnChanges {
    @ViewChild('fileUploader', { static: false }) fileUploader: DxFileUploaderComponent;
    @ViewChild('propertyCommunityDocuments', { static: false })
    propertyCommunityDocuments: PropertyCommunityDocumentsComponent;
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

    @Input() communityId: number;
    @Input() customerId: string;
    @Input() viewOnly = false;
    @Input() attachments: Array<AttachmentRawDto> = [];
    @Input() allowBulkSave = false;
    @Input() isDeleteEnabled = true;

    @Output() onAttachmentUpdate = new EventEmitter<AttachmentRawDto[]>();
    @Output() onViewAttachment = new EventEmitter<any[]>();
    @Output() onSaveAttachments = new EventEmitter<AttachmentRawDto[]>();

    attachmentOptions: any[];

    showAttachments = false;
    selectedAttachments: any[] = [];
    isNewAttachmentExists = false;
    attachmentCount: number;
    finalAttachments: AttachmentRawDto[] = [];
    attachmentIcon = IconConstants.ATTACHMENT;

    constructor(private readonly fileReaderService: FileReaderService) {}

    ngOnInit() {
        this.prepareAttachmentOptions();
        if (!!this.attachments && this.attachments.length > 0) {
            this.prepareExistingAttachments();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.attachments && changes.attachments.currentValue) {
            this.attachments = changes.attachments.currentValue;
            this.prepareAttachmentOptions();
            if (!!this.attachments && this.attachments.length > 0) {
                this.prepareExistingAttachments();
            }
        }
    }

    onViewAttachmentClick(): void {
        this.showAttachments = true;
    }

    onAttachmentClick(event): void {
        const selectedSize = 0;
        switch (event.itemData.name) {
            case EmailConstants.ATTACH_LOCAL_FILE:
                const uploader: any = this.fileUploader.instance;
                uploader._isCustomClickEvent = true;
                uploader._$fileInput[0].click();
                break;
            case EmailConstants.ATTACH_COMMUNITY_DOC:
                if (!!this.communityId) {
                    this.propertyCommunityDocuments.show(event.itemData.name, this.communityId, null, selectedSize);
                }
                break;
            case EmailConstants.ATTACH_OWNER_DOC:
                if (!!this.communityId && !!this.customerId) {
                    this.propertyCommunityDocuments.show(
                        event.itemData.name,
                        this.communityId,
                        this.customerId,
                        selectedSize
                    );
                }
                break;
        }
    }

    onAttachDocuments(attachments: AttachmentRawDto[]): void {
        if (Utils.notNullOrEmpty(attachments)) {
            attachments.forEach((document) => {
                if (!this.isAttachmentExists(document)) {
                    document.action = ActionConstants.ADD;
                    this.selectedAttachments.push(document);
                }
            });
        } else {
            this.selectedAttachments = attachments;
        }
        this.updateAttachments();
    }

    onAttachmentsSelect(event: any): void {
        const localAttachments = event.value;

        localAttachments.forEach((attachment) => {
            if (!this.isAttachmentExists(attachment)) {
                attachment.action = ActionConstants.ADD;
                this.fileReaderService.readFileToBase64(attachment).subscribe((base64Data) => {
                    attachment.fileBase64String = base64Data;
                    this.selectedAttachments.push(attachment);
                    this.updateAttachments();
                });
            } else {
                this.messageBox.showErrorMessage(
                    'Error',
                    `The selected file allready exist please select another file`
                );
            }
        });
    }

    onViewerClose(): void {
        this.showAttachments = false;
    }

    onSaveClick(): void {
        this.showAttachments = false;
        this.onSaveAttachments.emit(this.finalAttachments);
    }

    private isAttachmentExists(file: any): boolean {
        let fileExists = false;
        this.selectedAttachments.forEach((attachment) => {
            if (
                (!!file.fileId && !!attachment.fileId && file.fileId === attachment.fileId) ||
                (!!file.fileName && !!attachment.fileName && file.fileName === attachment.fileName) ||
                (!!file.name && !!attachment.name && file.name === attachment.name) ||
                (!!file.name &&
                    !!attachment.fileName &&
                    file.name === attachment.fileName &&
                    attachment.action === null)
            ) {
                fileExists = true;
            }
        });

        return fileExists;
    }

    private updateAttachments(): void {
        const activeAttachments = this.selectedAttachments.filter((d) => d.action !== ActionConstants.DELETE);
        this.attachmentCount = activeAttachments.length;
        this.isNewAttachmentExists = true;
        this.finalAttachments = [];
        if (Utils.notNullOrEmpty(this.selectedAttachments)) {
            this.selectedAttachments.forEach((document) => {
                const fileObj: AttachmentRawDto = new AttachmentRawDto();
                fileObj.action = !!document.action ? document.action : '';
                if (!!document.fileId) {
                    fileObj.fileId = document.fileId;
                    fileObj.communityId = document.communityId;
                    fileObj.customerId = document.customerId;
                    fileObj.fileName = document.fileName;
                } else {
                    fileObj.communityId = this.communityId;
                    fileObj.customerId = this.customerId;
                    fileObj.fileBase64String = document.fileBase64String;
                    fileObj.fileName = document.name;
                    fileObj.fileId = null;
                }
                this.finalAttachments.push(fileObj);
            });
        }

        this.onAttachmentUpdate.emit(this.finalAttachments);
        this.onViewAttachment.emit(this.selectedAttachments);
    }

    private prepareAttachmentOptions(): void {
        this.attachmentOptions = [
            {
                name: '',
                text: '',
                items: [
                    {
                        name: EmailConstants.ATTACH_LOCAL_FILE,
                        text: EmailConstants.ATTACH_LOCAL_FILE,
                        items: []
                    }
                ]
            }
        ];

        if (Utils.notNullAndDefined(this.communityId)) {
            this.attachmentOptions[0].items.push({
                name: EmailConstants.ATTACH_COMMUNITY_DOC,
                text: EmailConstants.ATTACH_COMMUNITY_DOC,
                items: []
            });
        }

        if (!!this.customerId) {
            this.attachmentOptions[0].items.push({
                name: EmailConstants.ATTACH_OWNER_DOC,
                text: EmailConstants.ATTACH_OWNER_DOC
            });
        }
    }

    private prepareExistingAttachments(): void {
        this.selectedAttachments = Object.assign(this.selectedAttachments, this.attachments);
        this.attachmentCount = this.selectedAttachments.length;
    }
}
